import type { ListBuilderConfiguration } from '../types';
import { getQuantityColumn } from './item.config';

/**
 * Price Contract Item list configuration
 * Shows items associated with a price contract
 * Features dynamic color rules and edit/delete actions
 * Converted from JSON to TypeScript for type safety
 */
export const PRICE_CONTRACT_ITEM_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-price-contract-items-all',
			cacheTTL: 600000,
			name: 'All Price Contract Items',
			type: 'default',
			active: true,
			bulkActionTemplate: 'priceContractItems',
			columns: [
				{
					id: 'description-manufacturer',
					fields: [
						{
							id: 'description-field',
							link: {
								type: 'custom',
								enabled: true,
								urlTemplate: '/items/{item.id}',
							},
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'item.description',
									objectTable: 'price_contract_item',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-1',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'manufacturer-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'item.manufacturer',
									objectTable: 'price_contract_item',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-2',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							// Hazmat / refrigerated / short-date / DEA badges, as on the
							// main item list. The component derives everything from row
							// data, so the bound field only has to be always-present.
							id: 'item-attribute-icons',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'item.id',
									objectTable: 'price_contract_item',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'custom',
							customComponent: { id: 'shop-item-attribute-icons' },
						},
					],
					headers: [
						{ id: 'h1-1', text: 'Name' },
						{ id: 'h1-2', text: 'Manufacturer' },
					],
					// The only flexible column (PD-16869). Every other column has a
					// numeric `width`, which the table treats as fixed: those keep
					// their size, this one takes whatever is left down to 200px, and
					// below that the table scrolls sideways rather than squeezing a
					// price or cost narrower than its text. A fixed width must still
					// fit its column's header and typical values, or the content
					// overflows it at every window size.
					minWidth: 200,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'description',
							objectTable: 'item',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'flexible',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'item-number',
					fields: [
						{
							id: 'item-number-field',
							dataConfig: {
								type: 'conditional',
								conditions: [
									{
										field: 'item.externalId2',
										operator: 'not_equals',
										value: null,
										dataConfig: {
											type: 'field',
											field: {
												fieldName: 'item.externalId2',
												objectTable: 'price_contract_item',
											},
										},
									},
									{
										field: 'item.externalId',
										operator: 'not_equals',
										value: null,
										dataConfig: {
											type: 'field',
											field: {
												fieldName: 'item.externalId',
												objectTable: 'price_contract_item',
											},
										},
									},
								],
								defaultDataConfig: {
									type: 'field',
									field: {
										fieldName: 'item.id',
										objectTable: 'price_contract_item',
									},
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'item-number-lb1',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textMuted',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'ndc-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'ndc',
									objectTable: 'item',
								},
								defaultValue: '-',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'item-number-lb2',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textMuted',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'category-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'category',
									objectTable: 'item',
								},
								defaultValue: '-',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{ id: 'h2', text: 'Item #' },
						{ id: 'h2-ndc', text: 'NDC' },
						{ id: 'h2-category', text: 'Category' },
					],
					width: 120,
					sortable: false,
					alignment: 'left',
					widthConfig: {
						mode: 'fixed',
						preset: 'small',
					},
					verticalAlignment: 'top',
				},
				// On Hand / Soonest Exp / On Backorder, placed right after the item
				// identifiers as on the main Items list (PD-16869). The rows come
				// from ItemsService.getAll, so On Hand is the same sum over the
				// user's warehouses that the main list shows. The main list's 80px
				// is too narrow for the "Soonest Exp" header plus its sort icon here,
				// so this list sets its own width.
				{
					...getQuantityColumn(),
					width: 160,
					widthConfig: {
						mode: 'fixed',
						preset: 'small',
					},
				},
				{
					id: 'minimum-quantity',
					fields: [
						{
							id: 'minimum-quantity-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'calculated.minimumQuantityFormatted',
									objectTable: 'price_contract_item',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h5', text: 'Min Order Qty' }],
					width: 125,
					sortable: false,
					alignment: 'center',
					widthConfig: {
						mode: 'fixed',
						preset: 'small',
					},
					verticalAlignment: 'middle',
				},
				{
					// When the contract doesn't affect price (affectsPrice false), the
					// three content fields hide via conditionalVisibility and a single
					// "-" placeholder shows instead of "- / Individual Price / -".
					// (The placeholder is needed because the column renderer's
					// empty-state "-" only fires when a column has zero field elements,
					// not when its fields conditionally render nothing.)
					id: 'price',
					fields: [
						{
							id: 'price-empty-placeholder',
							dataConfig: {
								type: 'custom_text',
								customText: '-',
							},
							conditionalVisibility: {
								field: 'affectsPrice',
								condition: 'not_equals',
								value: true,
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'contract-price-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'calculated.contractPriceFormatted',
									objectTable: 'price_contract_item',
								},
							},
							conditionalVisibility: {
								field: 'affectsPrice',
								condition: 'equals',
								value: true,
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-1',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'price-origin-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'calculated.priceOrigin',
									objectTable: 'price_contract_item',
								},
								defaultValue: 'Individual Price',
							},
							conditionalVisibility: {
								field: 'affectsPrice',
								condition: 'equals',
								value: true,
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-2',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'price-change-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'calculated.priceChangeFormatted',
									objectTable: 'price_contract_item',
								},
							},
							conditionalVisibility: {
								field: 'affectsPrice',
								condition: 'equals',
								value: true,
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'dynamic',
									colorField: 'calculated.priceChangeColor',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h6', text: 'Price' }],
					width: 150,
					sortable: false,
					alignment: 'right',
					widthConfig: {
						mode: 'fixed',
						preset: 'medium',
					},
					verticalAlignment: 'top',
				},
				{
					// Cost, mirroring the Price column. The whole column — header and
					// cells — is hidden for users without the cost-view permission,
					// and the backend only enriches these fields for permitted
					// callers.
					//
					// Three mutually exclusive states per row (PD-14150):
					//   • the contract overrides acquisition cost -> the calculated
					//     contract cost, labelled "Acquisition:", with the rule that
					//     produced it underneath;
					//   • it does not, but the item has its own acquisition cost ->
					//     that value, labelled "Item cost:" and rendered muted so the
					//     two can never be mistaken for each other. This mirrors the
					//     main item list's Item Cost column, which is the value users
					//     expect to see here;
					//   • neither -> a single "-" placeholder.
					//
					// Sell cost stays contract-only, with no item fallback — the main
					// item list shows no sell cost, so there is nothing to mirror.
					id: 'cost',
					requiredPermission: 'price_contracts:costs:view',
					fields: [
						{
							id: 'cost-empty-placeholder',
							dataConfig: {
								type: 'custom_text',
								customText: '-',
							},
							conditionalVisibility: {
								field: 'showsCost',
								condition: 'not_equals',
								value: true,
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'acquisition-cost-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'calculated.acquisitionCostFormatted',
									objectTable: 'price_contract_item',
								},
							},
							prefix: 'Acquisition: ',
							conditionalVisibility: {
								field: 'affectsAcquisitionCost',
								condition: 'equals',
								value: true,
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'cost-line-break-1',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'acquisition-cost-description-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'calculated.acquisitionCostDescription',
									objectTable: 'price_contract_item',
								},
							},
							conditionalVisibility: {
								field: 'affectsAcquisitionCost',
								condition: 'equals',
								value: true,
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'cost-line-break-2',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'item-acquisition-cost-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'calculated.itemAcquisitionCostFormatted',
									objectTable: 'price_contract_item',
								},
							},
							prefix: 'Item cost: ',
							conditionalVisibility: {
								field: 'showsItemAcquisitionCost',
								condition: 'equals',
								value: true,
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'cost-line-break-3',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'sell-cost-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'calculated.sellCostFormatted',
									objectTable: 'price_contract_item',
								},
							},
							prefix: 'Sell: ',
							conditionalVisibility: {
								field: 'affectsSellCost',
								condition: 'equals',
								value: true,
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'cost-line-break-4',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'sell-cost-description-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'calculated.sellCostDescription',
									objectTable: 'price_contract_item',
								},
							},
							conditionalVisibility: {
								field: 'affectsSellCost',
								condition: 'equals',
								value: true,
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h7', text: 'Cost' }],
					width: 160,
					sortable: false,
					alignment: 'right',
					widthConfig: {
						mode: 'fixed',
						preset: 'medium',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'actions',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'edit',
									callbackName: 'edit',
									variant: 'primary',
									visibility: 'visible',
									conditionalVisibility: {
										field: 'linkType',
										condition: 'equals',
										value: 'direct',
									},
								},
								{
									id: 'delete-action',
									label: 'Delete',
									iconName: 'trash',
									callbackName: 'delete',
									variant: 'danger',
									visibility: 'visible',
									conditionalVisibility: {
										field: 'linkType',
										condition: 'equals',
										value: 'direct',
									},
								},
							],
						},
					],
					headers: [{ id: 'actions-header', text: '' }],
					width: 80,
					align: 'center',
					sortable: false,
				},
			],
			defaultSortId: 'column-description-manufacturer-asc',
			// No authored requiredFields: the server derives the field set from the
			// resolved list's columns (extractRequiredFields), so columns added via
			// Configure List get their data selected without a code change
			// (PD-15409). The fields the enrichment itself needs are guaranteed
			// server-side (resolveContractItemSelectFields' baseline).
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 50,
		pageSizeOptions: [25, 50, 100, 200],
	},
	filters: {
		activeFilters: [
			'itemSearch',
			'itemCompany',
			'itemCategories',
			'itemManufacturers',
			'itemGroups',
			'itemStatus',
			'stock_status',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/items/:id',
			objectLabel: 'Item',
			objectTable: 'item',
			// Every row is an item, and `fkItemId` is the key that always holds
			// its id — NOT `id`, which the enrichment overwrites with the
			// price_contract_item link id when the item is linked directly.
			// Declaring it is what lets the field picker offer the whole item
			// table (NDC, cost, custom fields) instead of only the curated list
			// below, and what lets the server load those columns (PD-14150).
			rowIdField: 'fkItemId',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'description', fieldLabel: 'Description' },
				{ fieldName: 'manufacturer', fieldLabel: 'Manufacturer' },
				// Labelled as on the main Items list (PD-16869). externalId is the
				// ERP's internal id; the item number customers know is externalId2.
				// Labelling externalId "Item #" invited binding the Item # column to
				// the internal id.
				{ fieldName: 'externalId', fieldLabel: 'External ID' },
				{ fieldName: 'externalId2', fieldLabel: 'SKU' },
				{ fieldName: 'price', fieldLabel: 'Price' },
				// Curated labels for the size pair (PD-15409) — both render from
				// the item columns of the same name and stay off by default.
				{ fieldName: 'sizeCount', fieldLabel: 'Size' },
				{ fieldName: 'unitOfMeasure', fieldLabel: 'Unit of Measure' },
				// Curated so the picker reads "NDC" rather than the metadata's
				// auto-generated "Ndc" (PD-14150).
				{ fieldName: 'ndc', fieldLabel: 'NDC' },
				{ fieldName: 'category', fieldLabel: 'Category' },
			],
		},
	],
};
